import axios from 'axios';
import dayjs from 'dayjs';
import { Plugin } from '@nuxt/types';
import { loadScripts } from '@root/common/utils/scriptLoader';
import { resolvePromises } from '@root/common/utils/resolvePromises';
import Service from '@root/common/base/Service';

interface GaaPlugin {
  initGaa(handleUnlockArticle: () => Promise<string>, handleShowPaywall: () => Promise<void>): void;
}

declare module 'vue/types/vue' {
  interface Vue {
    $gaa: GaaPlugin;
  }
}

interface AnonymousNoAccess {
  granted: false;
}

interface RegisteredNoAccess {
  id: string;
  registrationTimestamp: number;
  granted: false;
}

interface RegisteredSubscriber {
  id: string;
  registrationTimestamp: number;
  subscriptionTimestamp: number;
  granted: true;
  grantReason: 'SUBSCRIBER';
}

type UserState = AnonymousNoAccess | RegisteredNoAccess | RegisteredSubscriber;

interface GaaMeteringInitArgument {
  authorizationUrl: string;
  userState: UserState;
  allowedReferrers: string[];
  handleLoginPromise: Promise<UserState>;
  publisherEntitlementPromise: Promise<UserState>;
  unlockArticle: () => Promise<string>;
  showPaywall: () => Promise<void>;
  shouldInitializeSwG: boolean;
}

interface GaaWindow extends Window {
  GaaMetering: {
    init(args: GaaMeteringInitArgument): void;
    getLoginPromise(): Promise<void>;
    getGaaUserPromise(): Promise<any>;
  };
  GaaMeteringRegwall: {
    remove(): void;
  };
  SWG: any;
  tp: any;
}

declare let window: GaaWindow;

const pianoOrigins = ['https://auth.delfi.lt', 'https://vx.delfi.lt', 'https://sandbox.piano.io'];

// SWG events map for analytics
const swgEvents: Record<number, string> = {
  1: 'IMPRESSION_PAYWALL',
  21: 'IMPRESSION_METER_TOAST',
  23: 'IMPRESSION_SHOWCASE_REGWALL',
  1060: 'ACTION_SHOWCASE_REGWALL_SIWG_CLICK',
  1036: 'ACTION_SHOWCASE_REGWALL_EXISTING_ACCOUNT_CLICK',
  1025: 'ACTION_METER_TOAST_SUBSCRIBE_CLICK',
  3009: 'EVENT_NO_ENTITLEMENTS',
  3011: 'EVENT_OFFERED_METER',
};

const gaa: Plugin = async (ctx, inject) => {
  const { store, app } = ctx;

  // Confidential Docs, access needed - https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?feed=server-side#pseudo-code
  const isShowcase = () => {
    const referrer = document.referrer;

    if (!referrer) {
      return false;
    }

    const url = new URL(window.location.href);
    const gaaParamsList = ['gaa_at', 'gaa_ts', 'gaa_n', 'gaa_sig'];
    const gaaParams: Record<string, string | undefined> = gaaParamsList.reduce((acc: Record<string, string | undefined>, param) => {
      acc[param] = url.searchParams.get(param) || '';

      return acc;
    }, {});

    return gaaParams.gaa_at && gaaParams.gaa_ts && gaaParams.gaa_n && gaaParams.gaa_sig;
  };

  const loadGaaScripts = async (): Promise<boolean> => {
    const gsiScript = () => loadScripts('https://accounts.google.com/gsi/client', { async: true, defer: true });
    const swgScript = () => loadScripts('https://news.google.com/swg/js/v1/swg.js', { async: true, 'subscriptions-control': 'manual' });
    const swgGaaScript = () => loadScripts('https://news.google.com/swg/js/v1/swg-gaa.js', { async: true });

    const scriptPromises = await Promise.allSettled([gsiScript(), swgScript(), swgGaaScript()]);
    return scriptPromises.every((scriptPromise) => scriptPromise.status === 'fulfilled');
  };

  const getUserRegistrationTimestamp = async (): Promise<number> => {
    const baseURL = 'https://api-eu.piano.io';
    const defaultTimestamp = dayjs().date(1).unix();

    const aid = store.state.piano.config.aid;
    const user_token = store.state.piano.token;
    const user_provider = 'piano_id';

    if (!aid || !user_token) {
      return defaultTimestamp;
    }

    const pianoClient = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        aid,
        user_token,
        user_provider,
      },
    });

    const result = await pianoClient.post('/api/v3/anon/user/get');
    const create_date = result.data?.user?.create_date;

    if (create_date) {
      return create_date;
    }

    return defaultTimestamp;
  };

  const getUserStateForActiveArticle = async () => {
    let userState: UserState = {
      granted: false,
    };

    const paywall = store.state.article.activeArticle?.content.paywall;
    const paywallEnabled = paywall?.enabled;
    const paywallAccess = paywall?.access;
    const articleAccess = !paywallEnabled || (paywallEnabled && paywallAccess);
    const registrationTimestamp = await getUserRegistrationTimestamp();
    const subscriptionStartDate = store.state.piano.subscriptionInfo?.startDate;

    if (articleAccess && subscriptionStartDate) {
      userState = {
        id: store.state.piano.profile.uid,
        registrationTimestamp,
        subscriptionTimestamp: Math.floor(new Date(subscriptionStartDate).getTime() / 1000),
        granted: true,
        grantReason: 'SUBSCRIBER',
      };
    } else if (store.state.piano.isLoggedIn) {
      userState = {
        id: store.state.piano.profile.uid,
        registrationTimestamp,
        granted: false,
      };
    }

    return userState;
  };

  const getAuthorizationUrl = async (aid: string, social_type: string, redirect_uri: string): Promise<string | null> => {
    const service = new Service();
    const apiProvider = service.createProvider('HTTP');
    try {
      const response = await apiProvider.request<string>({
        url: `/api/piano/generate-social-login-redirect-url`,
        method: 'post',
        data: { aid, social_type, redirect_uri },
      });

      return response;
    } catch (error) {
      console.log(error);
    }

    return null;
  };

  const completeSocialLogin = async (aid: string, response_id: string): Promise<Record<string, any> | null> => {
    const service = new Service();
    const apiProvider = service.createProvider('HTTP');
    try {
      const response = await apiProvider.request<Record<string, any>>({
        url: `/api/piano/complete-social-login`,
        method: 'post',
        data: { aid, response_id },
      });

      return response;
    } catch (error) {
      console.log(error);
    }

    return null;
  };

  const processCodeAdditional = async (aid: string, additional_input_state: string): Promise<Record<string, any> | null> => {
    const service = new Service();
    const apiProvider = service.createProvider('HTTP');
    try {
      const response = await apiProvider.request<Record<string, any>>({
        url: `/api/piano/process-code-additional`,
        method: 'post',
        data: { aid, additional_input_state },
      });

      return response;
    } catch (error) {
      console.log(error);
    }

    return null;
  };

  const initGaa = async (handleUnlockArticle: () => Promise<string>, handleShowPaywall: () => Promise<void>) => {
    if (!isShowcase()) {
      return;
    }

    const scriptsLoadResult = await loadGaaScripts();

    if (!scriptsLoadResult) {
      console.error('Failed to load GAA scripts');
      return;
    }

    const resolveOnLoginSuccessOrLoginClose = () =>
      new Promise<UserState>((resolve) => {
        const storeSubscription = store.subscribeAction((action) => {
          if (action.type === 'piano/initUser' && action.payload === 'loginSuccess') {
            window.removeEventListener('message', listenerHandler);

            const storeWatch = store.watch(
              (state) => state.piano.profile,
              async () => {
                resolve(getUserStateForActiveArticle());

                await resolvePromises();
                storeSubscription();
                storeWatch();
              }
            );
          }
        });

        const listenerHandler = async ({ data, origin }: { data: string; origin: string }) => {
          if (!pianoOrigins.includes(origin)) {
            return;
          }

          let parsedParams: any = undefined;

          try {
            parsedParams = JSON.parse(data);
          } catch {
            return;
          }

          if (parsedParams?.event === 'closed') {
            resolve(getUserStateForActiveArticle());

            window.removeEventListener('message', listenerHandler);
            await resolvePromises();
            storeSubscription();
          }
        };

        window.addEventListener('message', listenerHandler);
      });

    const isScriptInitedWatcher = store.watch(
      (state) => state.piano.isScriptInited,
      async (isScriptInited: boolean) => {
        if (!isScriptInited) {
          return;
        }

        const meta = app.$channelConfig('meta');
        const baseUrl = new URL(meta.base.baseUrl);

        const { piano } = app.$channelConfig('features');
        const aid = piano.account.aid;

        const authorizationUrl = await getAuthorizationUrl(aid, 'GOOGLE', location.href);
        if (!authorizationUrl) {
          return;
        }

        // Complete started social login
        const url = new URL(location.href);
        const response_id = url.searchParams.get('response_id');

        if (response_id) {
          completeSocialLogin(aid, response_id).then((result) => {
            const redirect_uri = result?.redirect_uri;

            // Set required consents with additional request, if needed
            if (result?.status === 'additional_input' && result?.additional_input_state) {
              const additional_input_state = result.additional_input_state;
              processCodeAdditional(aid, additional_input_state).then((additionalResult) => {
                // Login user with acquired token
                if (additionalResult?.access_token) {
                  const token = additionalResult.access_token;
                  window.tp.pianoId.loginByToken(token);
                }

                setTimeout(() => (location.href = redirect_uri), 500);
              });
            }

            // Login user with token, if user exists and token is provided
            if (result?.status === 'ok' && result.access_token) {
              const token = result.access_token;
              window.tp.pianoId.loginByToken(token);

              setTimeout(() => (location.href = redirect_uri), 500);
            }
          });
        }

        window.GaaMetering.init({
          authorizationUrl,
          userState: await getUserStateForActiveArticle(),
          allowedReferrers: [baseUrl.hostname, 'auth.delfi.lt'],
          handleLoginPromise: new Promise((resolve) => {
            window.GaaMetering.getLoginPromise().then(() => {
              window.GaaMeteringRegwall.remove();
              store.dispatch('piano/showLoginModal', 'login');

              resolveOnLoginSuccessOrLoginClose().then((userState) => resolve(userState));
            });
          }),
          publisherEntitlementPromise: new Promise((resolve) => resolve(getUserStateForActiveArticle())),
          unlockArticle: handleUnlockArticle,
          showPaywall: handleShowPaywall,
          shouldInitializeSwG: false,
        });

        // Analytics integration
        (window.SWG = window.SWG || []).push(function (subscriptions: any) {
          subscriptions.getEventManager().then((manager: any) => {
            manager.registerEventListener((event: any) => {
              if (swgEvents[event.eventType]) {
                const gaaEventType = swgEvents[event.eventType];
                app.$event.send('gaa-event', { gaaEventType });
              }
            });
          });
        });

        await resolvePromises();
        isScriptInitedWatcher();
      },
      {
        immediate: true,
      }
    );
  };

  const injectableObject: GaaPlugin = {
    initGaa,
  };

  inject('gaa', injectableObject);
};

export default gaa;
